// Shared by the Medicine at School form question, the Lookups page and the Medical tab, so a name
// is matched, searched and counted the same way everywhere. The server repeats the same check.

// Two names match when they are the same once capitals and spaces are ignored.
export const normaliseMedicineName = name => (name ?? '').replace(/\s+/g, '').toLowerCase();

// The medicine on the school's list that already uses this name, as its medicine name or as one of
// its brand names. A medicine being edited is left out, so it can keep its own names.
export const findMedicineNameClash = (medicines, name, leaveOutId = null) => {
    const key = normaliseMedicineName(name);
    if (!key) {
        return null;
    }

    return (medicines ?? []).find(medicine =>
        medicine.id !== leaveOutId &&
        (normaliseMedicineName(medicine.description) === key ||
            (medicine.brands ?? []).some(brand => normaliseMedicineName(brand.name) === key))
    ) ?? null;
};

// The pop-up wording agreed with Maria on 6 October 2026.
export const medicineClashHeading = (typed, schoolName) =>
    `${(typed ?? '').trim().replace(/\s+/g, ' ')} is included in the list of medicines held by ${schoolName}.`;

export const MEDICINE_CLASH_INSTRUCTION = 'Please go back and select it from the list.';

const byName = (a, b) => (a ?? '').localeCompare(b ?? '', 'en-GB', {numeric: true, sensitivity: 'base'});

// One search option per brand, plus one for the medicine itself, so a parent finds "Ventolin
// Evohaler (Salbutamol)" by typing either name. The usual unit and pack amount come with it.
// Medicines are in alphabetical order, each followed by its brands, with strengths in number order.
export const medicineSearchOptions = medicines => [...(medicines ?? [])]
    .sort((a, b) => byName(a.description, b.description))
    .flatMap(medicine => [
        {
            key: `${medicine.id}:`,
            medicineLookupId: medicine.id,
            medicineName: medicine.description,
            brandName: '',
            dosageUnit: medicine.dosageUnit ?? '',
            packAmount: medicine.packAmount ?? null,
            label: medicine.description
        },
        ...[...(medicine.brands ?? [])].sort((a, b) => byName(a.name, b.name)).map(brand => ({
            key: `${medicine.id}:${brand.name}`,
            medicineLookupId: medicine.id,
            medicineName: medicine.description,
            brandName: brand.name,
            dosageUnit: brand.dosageUnit ?? medicine.dosageUnit ?? '',
            packAmount: brand.packAmount ?? medicine.packAmount ?? null,
            label: `${brand.name} (${medicine.description})`
        }))
    ]);

// A parent may type "ph" or "f", as boxes print both, for example lisdexamphetamine and lisdexamfetamine.
const searchKey = name => normaliseMedicineName(name).replace(/ph/g, 'f');

export const matchesMedicineSearch = (option, text) => {
    const key = searchKey(text);
    return !key ||
        searchKey(option.medicineName).includes(key) ||
        searchKey(option.brandName).includes(key);
};

const capitalise = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

// "200 puffs", "1 pen", or "43 doses" for a medicine added before the unit was recorded.
export const countInUnit = (amount, unitKey, units) => {
    const unit = (units ?? []).find(u => u.key === unitKey);
    if (!unit) {
        return `${amount} ${amount === 1 ? 'dose' : 'doses'}`;
    }

    return `${amount} ${amount === 1 ? unit.singular : unit.plural}`;
};

// The label of the amount question: "Puffs in this box", "Tablets in this box".
export const amountQuestionLabel = (unitKey, units) => {
    const unit = (units ?? []).find(u => u.key === unitKey);
    return unit ? `${capitalise(unit.plural)} in this box` : 'Amount in this box';
};

export const unitName = (unitKey, units) => capitalise((units ?? []).find(u => u.key === unitKey)?.singular ?? '');

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'];

export const MONTH_NAMES = MONTHS;

// A box is entered by its expiry day, month and year. Where only the month and year are printed on the
// box, the day is left blank and the box is kept as expiring on the last day of that month. A box
// expiring on the last day reads as the month and year, any other day as the full date.
export const expiryMonthText = isoDate => {
    if (!isoDate) {
        return '';
    }

    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month) {
        return '';
    }

    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return day && day !== last ? `${day} ${MONTHS[month - 1]} ${year}` : `${MONTHS[month - 1]} ${year}`;
};

export const EXPIRY_DAY_LABEL = 'Expiry day, leave blank if only year and month are printed on the box';

// The days offered for the expiry day: every day of the chosen month, or 31 before a month is chosen.
export const expiryDays = (year, month) => {
    const last = year && month ? new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate() : 31;
    return Array.from({length: last}, (_, i) => String(i + 1));
};

// The expiry day kept on a box: blank where the box expires on the last day of its month.
export const expiryDayOf = isoDate => {
    const [year, month, day] = (isoDate ?? '').split('-').map(Number);
    if (!year || !month || !day) {
        return '';
    }

    return day === new Date(Date.UTC(year, month, 0)).getUTCDate() ? '' : String(day);
};

export const lastDayOfMonth = (year, month) => {
    const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

// The longest medicine name, brand name and directions the school's records hold.
export const MEDICINE_NAME_MAX = 200;
export const MEDICINE_DIRECTIONS_MAX = 1000;
// The largest amount one box may hold, far above any real box.
export const MEDICINE_BOX_MAX = 9999;
const AMOUNT_MESSAGE = `Enter the amount in this box, as a whole number up to ${MEDICINE_BOX_MAX.toLocaleString('en-GB')}`;

// The same checks the server makes on each box of the Medicine at School question, so a parent
// sees what is missing on the box itself before sending. One list of messages per box.
export const medicineStockErrors = (entries, now = new Date()) => (entries ?? []).map(entry => {
    const messages = [];
    const name = (entry?.medicineName ?? '').trim();
    const brand = (entry?.brandName ?? '').trim();
    const directions = (entry?.directions ?? '').trim();

    if (entry?.notListed ? name === '' : entry?.medicineLookupId == null) {
        messages.push(entry?.notListed
            ? 'Enter the medicine name'
            : 'Choose the medicine from the list, or tick that it is not on the list');
    }
    if (name.length > MEDICINE_NAME_MAX) {
        messages.push(`The medicine name cannot be longer than ${MEDICINE_NAME_MAX} characters`);
    }
    if (brand.length > MEDICINE_NAME_MAX) {
        messages.push(`The brand name cannot be longer than ${MEDICINE_NAME_MAX} characters`);
    }
    if (directions === '') {
        messages.push('Enter the directions, as printed on the pharmacy label');
    } else if (directions.length > MEDICINE_DIRECTIONS_MAX) {
        messages.push(`The directions cannot be longer than ${MEDICINE_DIRECTIONS_MAX} characters`);
    }
    if (!entry?.dosageUnit) {
        messages.push('Choose a dosage unit');
    }
    const amount = Number(entry?.amount);
    if (entry?.amount === '' || entry?.amount == null || !Number.isInteger(amount) || amount <= 0 || amount > MEDICINE_BOX_MAX) {
        messages.push(AMOUNT_MESSAGE);
    }
    const [year, month] = (entry?.expiry ?? '').split('-').map(Number);
    if (!year || !month || month < 1 || month > 12) {
        messages.push('Enter the expiry month and year, as printed on the box');
    } else if (year * 12 + month < now.getFullYear() * 12 + now.getMonth() + 1) {
        messages.push('This box has passed its expiry date. Please bring in a box that has not expired.');
    }

    return messages;
});

// The Lookups page search: a medicine matches on its own name or any of its brands, ignoring
// capitals and spaces. An empty search shows every medicine.
export const matchesLookupSearch = (medicine, text) => {
    const typed = normaliseMedicineName(text);
    if (!typed) {
        return true;
    }

    return normaliseMedicineName(medicine?.description).includes(typed) ||
        (medicine?.brands ?? []).some(brand => normaliseMedicineName(brand?.name).includes(typed));
};

// Only the medicines a school keeps active are offered on forms and profiles.
export const activeMedicines = medicines => (medicines ?? []).filter(medicine => medicine?.isActive !== false);
